import requestDefault from '@/shared/utils/request';
import type {
  MiddleIncomeCreditResultParams,
  MiddleIncomeCreditResultResponse,
  MiddleIncomeDispatchParams,
  MiddleIncomeDispatchResponse,
  MiddleIncomeCreditApplyParams,
  MiddleIncomeCreditApplyResponse,
  MiddleIncomeContractListParams,
  MiddleIncomeContractListResponse,
} from './types';
import { PageStatusEnum } from './types';

/**
 * 中收授信结果查询接口
 * @yapi https://yapi.hellobike.cn/project/5967/interface/api/427809
 * @description 查询授信结果，支持商城和贷超授信结果查询
 * @param params 请求参数
 * @returns Promise<MiddleIncomeCreditResultResponse | undefined>
 */
export const queryMiddleIncomeCreditResult = async (
  params: MiddleIncomeCreditResultParams,
): Promise<MiddleIncomeCreditResultResponse | undefined> => {
  const response = await requestDefault.postGate<MiddleIncomeCreditResultResponse>(
    'f.middle.income.credit.result',
    {
      ...params,
    },
    {
      showLoading: undefined,
      showErrMsg: undefined,
    },
  );

  // 兼容处理：如果返回的数据有status但没有pageStatus，则添加pageStatus字段
  if (response && 'status' in response && !('pageStatus' in response)) {
    return {
      ...response,
      pageStatus: response.status,
    } as MiddleIncomeCreditResultResponse;
  }

  return response;
};

/**
 * 中收业务统一分发接口
 * @yapi https://yapi.hellobike.cn/project/5967/interface/api/427167
 * @description 中收业务统一分发接口，根据业务场景返回可用的商城和贷超产品列表及申请策略
 * @param params 请求参数
 * @returns Promise<MiddleIncomeDispatchResponse | undefined>
 */
export const queryMiddleIncomeDispatch = async (
  params: MiddleIncomeDispatchParams,
): Promise<MiddleIncomeDispatchResponse | undefined> => requestDefault.postGate(
  'f.middle.income.dispatch',
  {
    ...params,
  },
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/**
 * 中收授信申请接口
 * @yapi https://yapi.hellobike.cn/project/5967/interface/api/427154
 * @description 根据编排策略调用授信申请，支持可配置的串行/并行策略
 * @param params 请求参数
 * @returns Promise<MiddleIncomeCreditApplyResponse | undefined>
 */
export const applyMiddleIncomeCredit = async (
  params: MiddleIncomeCreditApplyParams,
): Promise<MiddleIncomeCreditApplyResponse | undefined> => requestDefault.postGate(
  'f.middle.income.credit.apply',
  {
    ...params,
  },
  {
    showErrMsg: undefined,
  },
);

/**
 * 中收协议查询接口
 * @yapi https://yapi.hellobike.cn/project/5967/interface/api/427153
 * @description 并发调用商城和贷超协议查询接口，返回聚合协议列表
 * @param params 请求参数
 * @returns Promise<MiddleIncomeContractListResponse | undefined>
 */
export const queryMiddleIncomeContractList = async (
  params: MiddleIncomeContractListParams,
): Promise<MiddleIncomeContractListResponse | undefined> => requestDefault.postGate(
  'f.middle.income.contract.list',
  {
    ...params,
  },
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);
